/**
 * CompanyEditPage — owner edits identity / legal-numbers / contact info.
 * For approved companies, type+mode are LOCKED — changing them would
 * shift the capability set and require re-review. Owner needs to contact
 * admin instead.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Building2, Loader } from 'lucide-react';
import { useMyCompany, useUpdateCompany } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import CompanyForm from '@/components/companies/CompanyForm';

export default function CompanyEditPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: company, isLoading } = useMyCompany();
  const update = useUpdateCompany(company?.id || 0);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!company) {
    navigate('/company', { replace: true });
    return null;
  }

  const locked = company.status === 'approved' || company.status === 'pending_approval';

  return (
    <AppLayout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary">
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="bg-surface border border-border rounded-2xl p-6 max-w-2xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
          <Building2 size={20} /> {t('edit_company', 'تعديل بيانات الشركة')}
        </h1>
        <p className="text-text-secondary text-sm mb-5">
          {locked
            ? t('edit_company_locked_hint', 'لا يمكن تغيير النوع/الوضع بعد التقديم. للتغيير تواصل مع الإدارة.')
            : t('edit_company_hint', 'حدّث المعلومات قبل التقديم للمراجعة.')}
        </p>

        <CompanyForm
          initial={{
            companyType: company.company_type,
            operatorMode: company.operator_mode,
            name: company.name,
            nameAr: company.name_ar,
            commercialRegister: company.commercial_register,
            taxId: company.tax_id,
            importLicense: company.import_license,
            country: company.country,
            city: company.city,
            address: company.address,
            contactPhone: company.contact_phone,
            contactEmail: company.contact_email,
          }}
          submitLabel={t('save_changes', 'حفظ التعديلات')}
          loading={update.isPending}
          lockTypeAndMode={locked}
          onSubmit={async (values) => {
            await update.mutateAsync(values);
            navigate('/company', { replace: true });
          }}
        />
      </div>
    </AppLayout>
  );
}
